"use client";

import Link from "next/link";
import { FileText } from "lucide-react";
import { motion } from "framer-motion";
import { GradeBadge } from "@/components/common/grade-badge";
import { SectionReveal } from "@/components/common/section-reveal";
import { EASE_OUT_EXPO } from "@/lib/animations";
import type { TopEvidenceProcedure } from "@/types/api";
import { wikiLink } from "@/lib/url";

interface TopEvidenceShowcaseProps {
  items: TopEvidenceProcedure[];
}

export function TopEvidenceShowcase({ items }: TopEvidenceShowcaseProps) {
  if (!items || items.length === 0) return null;

  const maxArticles = Math.max(...items.map((i) => i.total_articles));

  return (
    <SectionReveal>
      <section className="py-8">
        <div className="mb-5 px-5">
          <h2 className="flex items-center gap-2.5 text-xl font-bold tracking-tight">
            <span className="h-5 w-1 rounded-sm bg-gradient-to-b from-teal-500 to-teal-700" />
            과학적으로 검증된 시술
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">논문이 가장 많은 시술 순위</p>
        </div>

        {/* 수평 스크롤 */}
        <div className="relative">
          <div className="scroll-fade-edges">
            <div className="scrollbar-none flex snap-x snap-mandatory gap-3.5 overflow-x-auto px-5 pb-4">
              {items.map((item, i) => (
                <motion.div
                  key={`${item.title}-${i}`}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4, ease: EASE_OUT_EXPO }}
                  className="snap-start"
                >
                  <Link
                    href={wikiLink(item.title)}
                    className="flex max-w-[240px] min-w-[220px] shrink-0 flex-col gap-3 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg active:scale-[0.97]"
                  >
                    {/* 상단: 순위 + 등급 */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-violet-400">#{i + 1}</span>
                      <GradeBadge grade={item.evidence_grade} size="md" />
                    </div>

                    {/* 제목 */}
                    <span className="text-[17px] leading-snug font-bold tracking-tight text-gray-900">
                      {item.title}
                    </span>

                    {/* 논문 수 바 */}
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-violet-400" strokeWidth={2} />
                        <span className="text-sm font-bold text-gray-900">
                          {item.total_articles.toLocaleString()}편
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-400 to-violet-600"
                          style={{
                            width: `${Math.max(10, (item.total_articles / maxArticles) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SectionReveal>
  );
}
